import { useRef } from "react";

import { ArrowLeftIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import useIsActive from "hooks/useIsActive";

import { ROUTES } from "utils/constants";

import Button from "components/Button";
import Container from "components/Container";
import Footer from "components/Footer";
import { Form, FormField } from "components/Form";
import Header from "components/Header";
import ImageCreator from "components/ImageCreator";
import Label from "components/Label";
import MainMenu from "components/MainMenu";
import { SelectComponent } from "components/Select";

import useTemplates from "./useTemplates";

export default function Templates() {
  useIsActive();
  const { t } = useTranslation();
  const {
    form,
    handleSubmit,
    control,
    isDirty,
    isSubmitting,
    onSubmit,
    templateId,
    templatePreview,
    handleChange,
    handleDownload,
    templates,
    getCompanySettings,
  } = useTemplates();

  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="bg-general-gray-200 ">
      <Header className="justify-between">
        <div className="flex items-center gap-2">
          <Link to={ROUTES.PROFILE} className="flex items-center cursor-pointer">
            <ArrowLeftIcon />
          </Link>
          {t("menu.findingsTemplate")}
        </div>
        <MainMenu />
      </Header>

      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-between min-h-full pb-0 lg:pb-10 bg-general-gray-200 "
          id="template-form"
        >
          <div className="flex justify-between bg-general-gray-100 border-b border-general-gray-300 fixed w-full top-16">
            <Container className="flex w-full justify-between !py-4 px-4 md:px-0">
              <Label>{t("profile.sampleFindings")}</Label>
              <FormField
                control={control}
                name="templateId"
                render={({ field }) => <SelectComponent field={field} options={templates} wrapperClassName="gap-0" />}
              />
            </Container>
          </div>

          <Container className="pt-[96px] px-4 lg:px-0">
            {templateId !== "100" ? (
              <div className="flex flex-col gap-4 items-center justify-center ">
                <ImageCreator data={templatePreview} alt="Doc preview" className="object-contain " />
                <Button type="button" onClick={handleDownload} className="w-full">
                  {t("button.download")}
                </Button>
              </div>
            ) : (
              <div className="py-4">
                <Label className="pb-4">{getCompanySettings.data.templatePath}</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={handleChange}
                />
                <Button type="button" className="w-full" onClick={() => fileInputRef.current?.click()} variant="dotted">
                  {t("button.upload")}
                </Button>
              </div>
            )}
          </Container>
        </form>
      </Form>

      <Footer className="md:justify-end">
        <Button className="w-full md:w-60" type="submit" disabled={!isDirty || isSubmitting} form="template-form">
          {t("button.saveChanges")}
        </Button>
      </Footer>
    </div>
  );
}
